import { isInteger, isNumber } from "lodash-es";

import type IField from "@mapmycustomers/shared/types/fieldModel/IField";
import type IFieldModel from "@mapmycustomers/shared/types/fieldModel/IFieldModel";
import type ViewState from "@mapmycustomers/shared/types/viewModel/ViewState";

import type { LocalSettings } from "@app/config/LocalSettings";
import localSettings from "@app/config/LocalSettings";
import type MapPersistedViewportState from "@app/types/map/MapPersistedViewportState";

const isFieldDefined = <T extends { field: IField | undefined }>(
  record: T
): record is T & { field: IField } => Boolean(record.field);

class RouteSettings {
  settings: LocalSettings;

  private readonly ROUTE_VIEWPORT = "__MMC_ROUTE_VIEWPORT__";

  private readonly UPCOMING_VIEW_STATE = "__MMC_ROUTE_UPCOMING_VIEW_STATE__";

  constructor(settings: LocalSettings) {
    this.settings = settings;
  }

  getViewportState = (): MapPersistedViewportState => {
    try {
      const json = this.settings.getValue(this.ROUTE_VIEWPORT) as null | string;
      const state: MapPersistedViewportState = json ? JSON.parse(json) : {};
      return {
        center:
          Array.isArray(state.center) && state.center.length === 2 && state.center.every(isNumber)
            ? state.center
            : undefined,
        zoom: isInteger(state.zoom) && state.zoom! > 0 ? state.zoom : undefined,
      };
    } catch {
      return {};
    }
  };

  updateViewportState = (state: MapPersistedViewportState) => {
    const current = this.getViewportState();
    this.settings.setValue(this.ROUTE_VIEWPORT, JSON.stringify({ ...current, ...state }));
  };

  getUpcomingViewSettings = (fieldModel: IFieldModel, defaultViewState: ViewState): ViewState => {
    const json = sessionStorage.getItem(this.UPCOMING_VIEW_STATE);
    if (!json) {
      return defaultViewState;
    }
    try {
      // parse JSON converting every field name back into a field instance
      const viewState: ViewState<IField | undefined> = JSON.parse(json, (key, value) =>
        key === "field" ? fieldModel.getByName(value) : value
      );
      // drop columns/sort whose fields no longer exist in the field model
      viewState.columns = viewState.columns.filter(isFieldDefined);
      viewState.sort = viewState.sort.filter(isFieldDefined);
      return viewState as ViewState;
    } catch {
      return defaultViewState;
    }
  };

  setUpcomingViewSettings = (viewState: ViewState) => {
    // convert all field instances to field names when saving
    const json = JSON.stringify(viewState, (key, value) =>
      key === "field" ? (value as IField).name : value
    );
    sessionStorage.setItem(this.UPCOMING_VIEW_STATE, json);
  };
}

const routeSettings = new RouteSettings(localSettings);

export default routeSettings;
